import { ReportSection } from '@/reports/index';
import { StaticMap } from '@/reports/maps/StaticMap/StaticMap';
import { useCensusData } from '@/reports/property/sections/DemographicsSection/useCensusData';
import { MrRogersNpanxxProps } from '@/reports/shared/useMrRogersNpanxx/interfaces';
import useMrRogersNpanxx from '@/reports/shared/useMrRogersNpanxx/useMrRogersNpanxx';
import { Text, Icons, Stack, styled } from '@/theme/index';
import { commaFormat } from '@/utils/commaFormat/commaFormat';
import { unformatPhone } from '@/utils/formatPhone';

interface PhoneLocationDetailsSectionProps {
  phoneNumber: string;
}

const StyledStack = styled(Stack)(({ theme }) => ({
  minHeight: '56px',
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '5px',
}));

export function PhoneLocationDetailsSection({
  phoneNumber,
}: PhoneLocationDetailsSectionProps) {
  // Constants

  const unformattedPhoneNumber = unformatPhone(phoneNumber);
  const npanxx = unformattedPhoneNumber.slice(0, 6);
  const npaNxxCities: string[] = [];
  const npaNxxZipcodes: string[] = [];

  // Hooks
  const npanxxResult = useMrRogersNpanxx({ phoneNumber });
  const { data: censusData } = useCensusData(undefined, npanxx);
  const population = censusData?.npanxx?.pop;
  const formattedPopulation = population
    ? commaFormat(population.toString())
    : '-';

  let latitude: number | undefined;
  let longitude: number | undefined;

  let cityStateString = '-';
  let npaNxxCitiesString = '-';
  let npaNxxZipsString = '-';

  if (npanxxResult.isSuccess && npanxxResult.data) {
    const data = npanxxResult.data;

    latitude = data[0].latitude;
    longitude = data[0].longitude;

    if (data[0].city && data[0].state) {
      cityStateString = `${data[0].city}, ${data[0].state}`;
    }

    data.forEach((d: MrRogersNpanxxProps) => {
      const { city, zipcode } = d;
      if (d.city && !npaNxxCities.includes(city)) {
        npaNxxCities.push(city);
      }
      if (d.zipcode && !npaNxxZipcodes.includes(zipcode)) {
        npaNxxZipcodes.push(zipcode);
      }
    });
    npaNxxZipsString = npaNxxZipcodes.join(', ');
    npaNxxCitiesString = npaNxxCities.join(', ');
  }

  return (
    <ReportSection
      title="Phone Location Details"
      sectionId="phone-location-details-section"
      hasContent={true}
    >
      <Text variant="body2">
        <Icons.FormatQuoteRounded
          sx={(theme) => ({
            fontSize: '17px',
            marginRight: theme.spacing(0.5),
          })}
        />
        Info about the location associated with the first 6 digits of the phone
        number, often referred to as the NPA/NXX.
      </Text>
      <Stack gap={1.25} flexWrap={'wrap'} flexDirection="row">
        <StyledStack
          flexDirection="row"
          alignItems="center"
          width={{ xs: '100%', md: '49%' }}
        >
          <Icons.LocationOnRounded
            color="primary"
            sx={(theme) => ({ marginLeft: theme.spacing(1.25) })}
          />
          <Stack ml={1.25}>
            <Text variant="body2">Location:</Text>
            <Text fontSize="15px" fontWeight="600">
              {cityStateString}
            </Text>
          </Stack>
        </StyledStack>
        <StyledStack
          flexDirection="row"
          alignItems="center"
          width={{ xs: '100%', md: '49%' }}
        >
          <Icons.EmojiFlags
            color="primary"
            sx={(theme) => ({ marginLeft: theme.spacing(1.25) })}
          />
          <Stack justifyContent={'center'} ml={1.25}>
            <Text variant="body2">Zip Codes Included:</Text>
            <Text fontSize="15px" fontWeight="600">
              {npaNxxZipsString}
            </Text>
          </Stack>
        </StyledStack>
        <StyledStack
          flexDirection="row"
          alignItems="center"
          width={{ xs: '100%', md: '49%' }}
        >
          <Icons.LocationCity
            color="primary"
            sx={(theme) => ({ marginLeft: theme.spacing(1.25) })}
          />
          <Stack justifyContent={'center'} ml={1.25}>
            <Text variant="body2">Cities Included:</Text>
            <Text fontSize="15px" fontWeight="600">
              {npaNxxCitiesString}
            </Text>
          </Stack>
        </StyledStack>
        <StyledStack
          flexDirection="row"
          alignItems="center"
          width={{ xs: '100%', md: '49%' }}
        >
          <Icons.Person
            color="primary"
            sx={(theme) => ({ marginLeft: theme.spacing(1.25) })}
          />
          <Stack justifyContent={'center'} ml={1.25}>
            <Text variant="body2">Population:</Text>
            <Text fontSize="15px" fontWeight="600">
              {formattedPopulation}
            </Text>
          </Stack>
        </StyledStack>
      </Stack>
      <Stack>
        {latitude && longitude && (
          <StaticMap
            zoomLevel={6}
            streetsView={true}
            longitude={longitude}
            latitude={latitude}
            width={768}
            height={350}
            withMarker
          />
        )}
      </Stack>
    </ReportSection>
  );
}
